import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { useSelector } from 'react-redux';
import WarrantyTypeDropdown from './WarrantyTypeDropdown';
import { InputNumber } from 'primereact/inputnumber';
import CascadingDropdowns from '../../../preApproval/components/CascadingDropdowns';
import { useNavigate } from 'react-router-dom';
import AlokasyonDropdown from './AlokasyonDropdown';
import DealerPackageDropdown from './DealerPackageDropdown';
import ButtonGroup from '../../../../../../components/ButtonGroup/ButtonGroup';
import { useLanguage } from '../../../../../../contexts/LanguageContext';
import CustomerDeclarationDialog from '../../../../../warranty-management/customer-declaration/components/dialogs/declaration-form/CustomerDeclarationDialog';
import { useGetDeclarationByIdQuery, useLazyGetDeclarationReportByIdQuery } from '../../../../../../api/customerDeclarationApi';
import PdfGenerationService from '../../../../../../utils/pdfGenerationService';
import { getClosingPdfTranslations, mapClosingFormData } from '../../../../customer-declaration/utils/pdfHelpers';
import ChassisWithExternalDropdown from '../../../../../../components/common/ChassisWithExternalDropdown';
import { formatDateOnly } from '../../../../../../utils/dateUtils';
import {
    useCalculateWarrantyClaimTotalsQuery,
    useGetWarrantyClaimsItemListQuery,
    useGetWarrantyClaimsLaborListQuery,
    useUpdateWarrantyClaimsMutation,
    useAddServiceBulletinItemsMutation,
    useGetWarrantyClaimsOtherCostListQuery,
    useAddDealerPackageItemsMutation,
    useGetWarrantyWarningByChassisQuery,
    useUpdateAllocationMutation
} from '../../../../../../api/endpoints/warrantyClaims';
import { selectUserRoles } from '../../../../../../features/auth/authSlice';
import { selectUserParams } from '../../../../../../store/slices/userParamsSlice';
import { Roles } from '../../../../../../utils/roleEnums';
import { WarrantyClaimStatus } from '../../../../../../utils/warrantyClaimsEnums';
import { usePermissions } from '../../../../../../hooks/usePermissions';
import ApprovalModal from './ApprovalModal';
import { useGetListVehicleAcceptanceFormQuery } from '../../../../../../api/endpoints/vehicleAcceptenceForm';
import { useGetAllPreStockInspectionQuery } from '../../../../../../api/endpoints/preStockInspection';
import FaultModesDropdown from '../../../../../../components/common/FaultModeDropdown';
import SubWarrantyTypeDropdown from '../../../../../../components/common/SubWarrantyTypeDropdown';
import WarrantyPaymentTypeRadio from '../../../../../../components/common/WarrantyPaymentTypeRadio';
import PreApprovalDropdown from '../../../../../../components/common/PreApprovalDropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { useGetSapBulletinByChassisNoQuery } from '../../../../../../api/endpoints/compound';
import ApprovalCommentsTable from './ApprovalCommentsTable';
import CostSummaryTables from './CostSummaryTables';
import { confirmDialog } from 'primereact/confirmdialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useGetWarrantyInformationByChassisNoQuery } from '../../../../../../api/endpoints/vehicleWarrantyInformationReport';
import VehicleWarrantyInfoDialog from '../warrantyClaimsDetailTabs/detailDialogForms/VehicleWarrantyInfoDialog';
import WarrantyWarningBanner from './WarrantyWarningBanner';
import FormField from './FormField';
import FormFieldPair from './FormFieldPair';

const FormWarrantyClaims = ({ id, data, editMode, submitted, formData, handleChange, saveWarrantyClaims, handleActionSave, resetForm, setSubmitted, isCreating, isUpdating, isActionLoading }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const navigate = useNavigate();
    const { canCreateCustomerDeclaration, hasWarrantyTeamRole } = usePermissions();
    const toast = useRef(null);

    const [showBulletNo, setShowBulletNo] = useState(false);
    const [actionType, setActionType] = useState(null);
    const userRoles = useSelector(selectUserRoles);
    const userParams = useSelector(selectUserParams);
    const isKmobilityUser = userParams?.isKmobilityUser || false;
    const [customerDeclarationDialogVisible, setCustomerDeclarationDialogVisible] = useState(false);
    const [customerDeclarationFormData, setCustomerDeclarationFormData] = useState({});
    const [approvalModalVisible, setApprovalModalVisible] = useState(false);
    const [approvalModalType, setApprovalModalType] = useState('approve'); // 'approve', 'reject', 'back'
    const [vehicleAcceptanceFormData, setVehicleAcceptanceFormData] = useState(null);
    const [shouldSearchVAF, setShouldSearchVAF] = useState(false);
    const [preStockInspectionData, setPreStockInspectionData] = useState(null);
    const [shouldSearchPSI, setShouldSearchPSI] = useState(false);
    const [selectedWarrantyTypeObject, setSelectedWarrantyTypeObject] = useState(null);
    const [subWarrantyTypeVisible, setSubWarrantyTypeVisible] = useState(null);
    const [showPmDropdown, setShowPmDropdown] = useState(false);
    const [warrantyInfoDialogVisible, setWarrantyInfoDialogVisible] = useState(false);

    // Validation states for cascading dropdowns and fault mode
    const [cascadingDropdownErrors, setCascadingDropdownErrors] = useState({
        mainCategory: false,
        secondCategory: false,
        failureMode: false
    });

    const handleShowSendModal = () => {
        setApprovalModalType('send');
        setApprovalModalVisible(true);
    };

    const handleSendConfirm = (comment) => {
        handleSubmit(1, comment);
        setApprovalModalVisible(false);
    };

    const handleShowReturnedUpdateModal = () => {
        setApprovalModalType('returnedUpdate');
        setApprovalModalVisible(true);
    };

    const handleReturnedUpdateConfirm = (comment) => {
        handleReturnedUpdate(comment);
        setApprovalModalVisible(false);
    };

    // Returned warranty claims update mutation
    const [updateWarrantyClaims, { isLoading: isReturnedUpdateLoading }] = useUpdateWarrantyClaimsMutation();
    const [isReturnedEditMode, setIsReturnedEditMode] = useState(false);

    // Check if warranty claim is returned (status RETURNED) and in edit mode
    const isReturnedEditable = data?.status === WarrantyClaimStatus.RETURNED && isReturnedEditMode;

    const [triggerGetDeclarationReportById] = useLazyGetDeclarationReportByIdQuery();
    const pdfService = React.useMemo(() => new PdfGenerationService(), []);

    const { data: customerDeclarationData, isLoading: isLoadingCustomerDeclaration } = useGetDeclarationByIdQuery(data?.customerStatementId, { skip: !customerDeclarationDialogVisible || !data?.customerStatementId });

    const { data: totalsData, isLoading: isLoadingTotals, refetch: refetchTotals } = useCalculateWarrantyClaimTotalsQuery({ warrantyClaimId: id }, { skip: !editMode || !id });

    const {
        data: apiWarrantyClaimsItemList,
        isLoading: isLoadingItems,
        refetch: refetchItems
    } = useGetWarrantyClaimsItemListQuery(
        {
            warrClaimId: id,
            PageSize: 1000,
            PageNumber: 1,
            LanguageId: getCurrentLanguageId()
        },
        { skip: !editMode || !id }
    );

    const {
        data: apiWarrantyClaimsLaborList,
        isLoading: isLoadingLabors,
        refetch: refetchLabors
    } = useGetWarrantyClaimsLaborListQuery(
        {
            WarrantyClaimsId: id,
            PageSize: 1000,
            PageNumber: 1,
            LanguageId: getCurrentLanguageId()
        },
        { skip: !editMode || !id }
    );

    const {
        data: apiWarrantyClaimsOtherCostList,
        isLoading: isLoadingOtherCosts,
        refetch: refetchOtherCosts
    } = useGetWarrantyClaimsOtherCostListQuery(
        {
            WarrantyClaimsId: id,
            PageSize: 1000,
            PageNumber: 1,
            LanguageId: getCurrentLanguageId()
        },
        { skip: !editMode || !id }
    );

    // Warranty information query
    const { data: warrantyInfoData, isLoading: isLoadingWarrantyInfo } = useGetWarrantyInformationByChassisNoQuery(
        {
            sasiNo: formData.chassisNo
        },
        {
            skip: !formData.chassisNo || !editMode
        }
    );

    // Warranty warning query - VAF/PSI claim'leri için şasi bazlı uyarı kontrolü
    const { data: warrantyWarningData, isLoading: isLoadingWarrantyWarning } = useGetWarrantyWarningByChassisQuery({ chassisNo: formData.chassisNo, claimId: id }, { skip: !formData.chassisNo || !editMode || !(shouldSearchVAF || shouldSearchPSI) });

    // Service Bulletin Items mutation
    const [addServiceBulletinItems, { isLoading: isAddingBulletinItems }] = useAddServiceBulletinItemsMutation();

    // Dealer Package Items mutation
    const [addDealerPackageItems, { isLoading: isAddingDealerPackageItems }] = useAddDealerPackageItemsMutation();

    // Vehicle Acceptance Form search query
    const { data: vehicleAcceptanceFormResponse, isLoading: isLoadingVAF } = useGetListVehicleAcceptanceFormQuery(
        {
            PageNumber: 1,
            PageSize: 10,
            languageId: 1,
            status: 30,
            chassisNo: formData.chassisNo
        },
        {
            skip: !shouldSearchVAF || !formData.chassisNo
        }
    );

    // Pre Stock Inspection search query
    const { data: preStockInspectionResponse, isLoading: isLoadingPSI } = useGetAllPreStockInspectionQuery(
        {
            pageNumber: 1,
            pageSize: 100,
            Vin: formData.chassisNo,
            Status: 10
        },
        {
            skip: !shouldSearchPSI || !formData.chassisNo
        }
    );

    // SAP Bulletin query - only fetch when Servis Bülteni is selected and chassisNo is available
    const { data: sapBulletinData = [], isLoading: isLoadingBulletins } = useGetSapBulletinByChassisNoQuery(
        { ChassisNo: formData.chassisNo },
        {
            skip: !showBulletNo || !formData.chassisNo || (editMode && !isReturnedEditable)
        }
    );

    // Transform bulletin data for dropdown
    const bulletinOptions = React.useMemo(() => {
        if (!Array.isArray(sapBulletinData)) return [];
        // Remove duplicates based on qmnum
        const uniqueBulletins = Array.from(new Map(sapBulletinData.map((item) => [item.qmnum, item])).values());
        return uniqueBulletins.map((item) => ({
            label: `${item.qmnum}${item.description ? ' - ' + item.description : ''}`,
            value: item.qmnum
        }));
    }, [sapBulletinData]);

    const authorizationCheckforWarrantyClaims =
        Array.isArray(userRoles) &&
        (userRoles.includes(Roles.WARRANTY_TEAM) ||
            userRoles.includes(Roles.WARRANTY_MANAGER) ||
            userRoles.includes(Roles.SSH_MANAGER) ||
            userRoles.includes(Roles.SSH_DIRECTOR) ||
            userRoles.includes(Roles.SSH_GENERAL_MANAGER) ||
            userRoles.includes(Roles.CEO) ||
            userRoles.includes(Roles.ADMIN));

    // Sadece Garanti Ekibi, Garanti Yöneticisi ve Admin onaylanmış kayıtlardaki alokasyonu güncelleyebilir
    const canUpdateAllocationOnApproved = Array.isArray(userRoles) && (userRoles.includes(Roles.WARRANTY_TEAM) || userRoles.includes(Roles.WARRANTY_MANAGER) || userRoles.includes(Roles.ADMIN));

    // Check if status allows showing allocation info as readonly
    const shouldShowAllocationReadonly = data?.status === WarrantyClaimStatus.ACCEPTED || data?.status === WarrantyClaimStatus.RETURNED || data?.status === WarrantyClaimStatus.REJECTED;

    // Faturası kesilmiş bir talebin alokasyon (ve benzeri) alanları artık değiştirilemez
    const isInvoiceIssued = Boolean(data?.hasInvoice);

    // Inline allocation edit (only when ACCEPTED + authorized)
    const [isAllocationEditMode, setIsAllocationEditMode] = useState(false);
    const [allocationDraft, setAllocationDraft] = useState(null);
    const [updateAllocation, { isLoading: isUpdatingAllocation }] = useUpdateAllocationMutation();

    const handleStartAllocationEdit = () => {
        if (isInvoiceIssued) {
            return;
        }
        setAllocationDraft(data?.alokasyon || formData?.alokasyon || null);
        setIsAllocationEditMode(true);
    };

    const handleCancelAllocationEdit = () => {
        setAllocationDraft(null);
        setIsAllocationEditMode(false);
    };

    const handleSaveAllocation = async () => {
        if (!allocationDraft || !id || isInvoiceIssued) {
            return;
        }

        try {
            const result = await updateAllocation({
                claimId: Number(id),
                alokasyon: Number(allocationDraft)
            }).unwrap();

            if (result && result.isSuccessful !== false) {
                handleChange('alokasyon', allocationDraft);
                toast.current?.show({
                    severity: 'success',
                    summary: th('common.success'),
                    detail: th('warrantyClaims.detail.messages.allocationUpdateSuccess'),
                    life: 3000
                });
                setIsAllocationEditMode(false);
                setAllocationDraft(null);
            } else {
                toast.current?.show({
                    severity: 'warn',
                    summary: th('common.warning'),
                    detail: result?.errorMessage || th('warrantyClaims.detail.messages.allocationUpdateError'),
                    life: 5000
                });
            }
        } catch (error) {
            console.error('Error updating allocation:', error);
            const errorMessage = error?.data?.errorMessage || error?.data?.message || th('warrantyClaims.detail.messages.allocationUpdateError');
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: errorMessage,
                life: 5000
            });
        }
    };

    // Initialize selectedWarrantyTypeObject when entering returned edit mode
    useEffect(() => {
        if (isReturnedEditable && data?.warrType && !selectedWarrantyTypeObject) {
            // Create a temporary warranty type object from existing data
            const tempWarrantyTypeObject = {
                id: data.warrantyType,
                code: data.warrType,
                text: data.warrTypeName || data.warrType,
                displayValue: data.warrTypeName || data.warrType
            };
            setSelectedWarrantyTypeObject(tempWarrantyTypeObject);
        }
    }, [isReturnedEditable, data?.warrType, data?.warrantyType, data?.warrTypeName, selectedWarrantyTypeObject]);

    useEffect(() => {
        // In readonly edit mode, use data.warrType (code) for all checks
        // In create mode or returned edit mode, use selectedWarrantyTypeObject.code
        const warrantyTypeCode = editMode && data?.warrType && !isReturnedEditable ? data.warrType.toUpperCase() : selectedWarrantyTypeObject?.code?.toUpperCase() || String(formData?.warrantyType);

        setShowBulletNo(warrantyTypeCode === 'B');
        setSubWarrantyTypeVisible(warrantyTypeCode === 'W' || warrantyTypeCode === 'EX');
        setShowPmDropdown(warrantyTypeCode === 'PM');

        // Check warranty type for VAF/PSI search in both create and edit modes
        let shouldSearchVAF = false;
        let shouldSearchPSI = false;

        if (editMode && data?.warrType && !isReturnedEditable) {
            // In readonly edit mode, check data.warrType from getById response
            const warrType = data.warrType.toUpperCase();
            shouldSearchVAF = warrType === 'VAF';
            shouldSearchPSI = warrType === 'PSI';
        } else if (!editMode || isReturnedEditable) {
            // In create mode or returned edit mode, use existing logic
            shouldSearchVAF = shouldTriggerVAFSearch(formData?.warrantyType);
            shouldSearchPSI = shouldTriggerPSISearch(formData?.warrantyType);
        }

        setShouldSearchVAF(shouldSearchVAF);
        setShouldSearchPSI(shouldSearchPSI);

        // Reset data when warranty type changes
        if (!shouldSearchVAF) {
            setVehicleAcceptanceFormData(null);
        }
        if (!shouldSearchPSI) {
            setPreStockInspectionData(null);
        }
    }, [formData.warrantyType, selectedWarrantyTypeObject, editMode, data?.warrType, isReturnedEditable]);

    // Function to check if warranty type should trigger VAF search
    const shouldTriggerVAFSearch = (warrantyType) => {
        if (!warrantyType) return false;

        // Check for specific IDs
        if (warrantyType === '1054' || warrantyType === '1052') {
            return true;
        }

        // Check for code VAF
        if (selectedWarrantyTypeObject && selectedWarrantyTypeObject.code) {
            const code = selectedWarrantyTypeObject.code.toUpperCase();
            if (code === 'VAF') {
                return true;
            }
        }

        return false;
    };

    // Function to check if warranty type should trigger PSI search
    const shouldTriggerPSISearch = (warrantyType) => {
        if (!warrantyType) return false;

        // Check for specific ID
        if (warrantyType === '1054') {
            return true;
        }

        // Check for code PSI
        if (selectedWarrantyTypeObject && selectedWarrantyTypeObject.code) {
            const code = selectedWarrantyTypeObject.code.toUpperCase();
            if (code === 'PSI') {
                return true;
            }
        }

        return false;
    };

    // Handler for warranty type changes
    const handleWarrantyTypeChange = (value, warrantyTypeObject) => {
        setSelectedWarrantyTypeObject(warrantyTypeObject);
        handleChange('warrantyType', value);
    };

    // Handler for cascading dropdown changes with validation clearing
    const handleCascadingDropdownChange = (field, value) => {
        handleChange(field, value);

        // Clear validation error for this field when user makes a selection
        if (cascadingDropdownErrors[field]) {
            setCascadingDropdownErrors((prev) => ({
                ...prev,
                [field]: false
            }));
        }
    };

    // Handler for bulletNo changes - adds service bulletin items when in returned edit mode
    const handleBulletNoChange = async (value) => {
        // Update formData
        handleChange('bulletNo', value);

        // If in returned edit mode, warranty type is 'B', and a bulletNo is selected
        if (isReturnedEditable && showBulletNo && value && id) {
            try {
                // Call the API to add service bulletin items
                const result = await addServiceBulletinItems({
                    claimId: Number(id),
                    bulletNo: value,
                    clearExistingItems: true
                }).unwrap();

                // Check if the API returned success
                if (result && result.isSuccessful) {
                    // invalidatesTags will automatically refetch the data, so we don't need manual refetch
                    // Show success message
                    toast.current?.show({
                        severity: 'success',
                        summary: th('common.success'),
                        detail: th('warrantyClaims.detail.messages.bulletinItemsAdded'),
                        life: 3000
                    });
                } else {
                    // API returned but with isSuccessful: false
                    toast.current?.show({
                        severity: 'warn',
                        summary: th('common.warning'),
                        detail: result?.errorMessage || th('warrantyClaims.detail.messages.bulletinItemsError'),
                        life: 5000
                    });
                }
            } catch (error) {
                console.error('Error adding service bulletin items:', error);
                // Only show error toast once
                const errorMessage = error?.data?.errorMessage || error?.data?.message || th('warrantyClaims.detail.messages.bulletinItemsError');
                toast.current?.show({
                    severity: 'error',
                    summary: th('common.error'),
                    detail: errorMessage,
                    life: 5000
                });
            }
        }
    };

    // Function to add dealer package items and refetch all tabs - called when both dealerPackageIds and packageWarrantyType are available
    const addDealerPackageItemsAndRefetchTabs = async (dealerPackageIds, packageWarrantyType) => {
        // Validate that both values are present - if not, don't call any API
        if (!dealerPackageIds || dealerPackageIds.length === 0 || !packageWarrantyType) {
            return;
        }

        // Only call API when in returned edit mode, warranty type is 'PM', and id exists
        if (isReturnedEditable && showPmDropdown && id) {
            try {
                for (const packageId of dealerPackageIds) {
                    await addDealerPackageItems({
                        claimId: Number(id),
                        dealerPackageId: Number(packageId),
                        packageWarrantyType: Number(packageWarrantyType),
                        clearExistingItems: false
                    }).unwrap();
                }

                await Promise.all([refetchItems(), refetchLabors(), refetchOtherCosts(), refetchTotals()]);

                toast.current?.show({
                    severity: 'success',
                    summary: th('common.success'),
                    detail: th('warrantyClaims.detail.messages.dealerPackageItemsAdded'),
                    life: 3000
                });
            } catch (error) {
                console.error('Error adding dealer package items:', error);
                const errorMessage = error?.data?.errorMessage || error?.data?.message || th('warrantyClaims.detail.messages.dealerPackageItemsError');
                toast.current?.show({
                    severity: 'error',
                    summary: th('common.error'),
                    detail: errorMessage,
                    life: 5000
                });
            }
        }
    };

    // Handler for dealerPackageIds changes
    const handleDealerPackageChange = async (value) => {
        handleChange('dealerPackageIds', value);
        await addDealerPackageItemsAndRefetchTabs(value, formData.packageWarrantyType);
    };

    // Handler for packageWarrantyType changes
    const handlePackageWarrantyTypeChange = async (value) => {
        handleChange('packageWarrantyType', value);
        await addDealerPackageItemsAndRefetchTabs(formData.dealerPackageIds, value);
    };

    // Handler for VAF navigation
    const handleVAFClick = () => {
        const navigationData = {
            targetModule: 'VAF',
            chassisNo: formData.chassisNo,
            triggeredFrom: 'warranty-claims',
            navigationId: Date.now().toString(),
            shouldNavigate: true
        };

        // localStorage'a kaydet (yeni sekme için)
        localStorage.setItem('crossModuleNavigation', JSON.stringify(navigationData));

        // Yeni tab'da VAF sayfasını aç
        window.open('/warranty-management/warranty-process-forms/vehicle-acceptance-form', '_blank');
    };

    // Handler for PSI navigation
    const handlePSIClick = () => {
        const navigationData = {
            targetModule: 'PSI',
            chassisNo: formData.chassisNo,
            triggeredFrom: 'warranty-claims',
            navigationId: Date.now().toString(),
            additionalData: { psiId: preStockInspectionData.id },
            shouldNavigate: true
        };

        // localStorage'a kaydet (yeni sekme için)
        localStorage.setItem('crossModuleNavigation', JSON.stringify(navigationData));

        // Yeni tab'da PSI detay sayfasını aç
        window.open(`/warranty-management/warranty-process-forms/pre-stock-inspection-checklist/${preStockInspectionData.id}`, '_blank');
    };

    // Handle Vehicle Acceptance Form response
    useEffect(() => {
        if (vehicleAcceptanceFormResponse && vehicleAcceptanceFormResponse.vehicleAcceptanceFormGetListQueryItems) {
            const items = vehicleAcceptanceFormResponse.vehicleAcceptanceFormGetListQueryItems;
            if (items.length > 0) {
                setVehicleAcceptanceFormData(items[0]); // Take the first result
            } else {
                setVehicleAcceptanceFormData(null);
            }
        }
    }, [vehicleAcceptanceFormResponse]);

    // Handle Pre Stock Inspection response
    useEffect(() => {
        if (preStockInspectionResponse && Array.isArray(preStockInspectionResponse)) {
            if (preStockInspectionResponse.length > 0) {
                setPreStockInspectionData(preStockInspectionResponse[0]); // Take the first result
            } else {
                setPreStockInspectionData(null);
            }
        }
    }, [preStockInspectionResponse]);

    useEffect(() => {
        if (customerDeclarationData && customerDeclarationDialogVisible) {
            setCustomerDeclarationFormData({
                id: customerDeclarationData.id,
                serviceDeliveryDate: customerDeclarationData.vehicleAcceptDate || null,
                chassisNumber: customerDeclarationData.chassisNo || '',
                customer: customerDeclarationData.customer || 0,
                customerName: customerDeclarationData.customerNameSurname || '',
                customerNameSurname: customerDeclarationData.customerNameSurname || '',
                customerPhone: customerDeclarationData.customerPhone || '',
                customerStatement: customerDeclarationData.customerStatement || '',
                kmInformation: customerDeclarationData.kmInformation || 0,
                plateNumber: customerDeclarationData.licensePlate || '',
                brand: customerDeclarationData.brand || '',
                model: customerDeclarationData.model || '',
                registrationDate: customerDeclarationData.dateOfTraffic || null,
                receivingPerson: customerDeclarationData.receivingPerson || '',
                receivingDeliveryPerson: customerDeclarationData.receivingDeliveryPerson || '',
                warrantyBeginDate: customerDeclarationData.warrantyBeginDate || null,
                warrantyEndDate: customerDeclarationData.warrantyEndDate || null,
                status: customerDeclarationData.status || 0,
                hasWarrantyClaim: customerDeclarationData.hasWarrantyClaim || false
            });
        }
    }, [customerDeclarationData, customerDeclarationDialogVisible]);

    const handleViewClosingFormPdf = useCallback(
        async (customerStatementId) => {
            try {
                const result = await triggerGetDeclarationReportById(customerStatementId).unwrap();
                if (result) {
                    const pdfData = mapClosingFormData(result);
                    const translations = getClosingPdfTranslations(th);
                    const pdfDoc = pdfService.generateRepairClosingFormPdf(pdfData, translations, { halfPage: true });
                    pdfService.viewPdf(pdfDoc);
                }
            } catch (error) {
                toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('warranty.customerDeclaration.messages.error'), life: 3000 });
            }
        },
        [triggerGetDeclarationReportById, pdfService, th]
    );

    const handleOpenCustomerDeclarationDialog = () => {
        setCustomerDeclarationDialogVisible(true);
    };

    const handleCloseCustomerDeclarationDialog = () => {
        setCustomerDeclarationDialogVisible(false);
        setCustomerDeclarationFormData({});
    };

    const returnBack = () => {
        navigate(-1);
    };

    const handleDelete = () => {
        confirmDialog({
            message: th('warrantyClaims.detail.buttons.deleteConfirmMessage'),
            header: th('warrantyClaims.detail.buttons.deleteConfirmHeader'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                await handleAction('delete');
            },
            rejectClassName: 'p-button-outlined'
        });
    };

    const handleSubmit = useCallback(
        async (status, sendToApprovalComment = '') => {
            setSubmitted(true);

            // Basic validation
            if (!formData.chassisNo || !formData.failureDate) return false;

            // Validation for bulletNo when showBulletNo is true (Servis Bülteni is selected)
            if (showBulletNo && !formData.bulletNo) {
                return false;
            }

            // Validation for subWarrantyType when warranty type is 'W' (Standart Garanti) or 'EX' (Extended Warranty)
            if ((String(formData?.warrantyType) === 'W' || String(formData?.warrantyType) === 'EX') && !formData.subWarrantyType) {
                return false;
            }

            // Validation for dealerPackageIds and packageWarrantyType when warranty type is 'PM'
            if (String(formData?.warrantyType) === 'PM' && (!formData.dealerPackageIds || formData.dealerPackageIds.length === 0 || !formData.packageWarrantyType)) {
                return false;
            }

            // Validation for preAppNo when warranty type is 'G' (Goodwill) - Ön Onay No zorunlu (create ekranı)
            if (!editMode && String(formData?.warrantyType) === 'G' && !formData.preAppNo) {
                return false;
            }

            // Validation for cascading dropdowns and fault mode in edit mode when status is DRAFT
            if (editMode && data?.status === WarrantyClaimStatus.DRAFT) {
                const dropdownValidation = {
                    mainCategory: !formData.mainCategory,
                    secondCategory: !formData.secondCategory,
                    failureMode: !formData.failureMode
                };

                setCascadingDropdownErrors(dropdownValidation);

                // Check if any dropdown is missing
                if (dropdownValidation.mainCategory || dropdownValidation.secondCategory || dropdownValidation.failureMode) {
                    return false;
                }
            }

            try {
                const payload = {
                    ...formData,
                    sendToApprovalComment: sendToApprovalComment?.trim() || ''
                };
                const success = await saveWarrantyClaims(payload, editMode, id, status);
                if (success) resetForm();
                return success;
            } catch (err) {
                console.error('Form submission error:', err);
                return false;
            }
        },
        [formData, saveWarrantyClaims, editMode, id, data?.status, data?.warrType, showBulletNo, showPmDropdown, selectedWarrantyTypeObject, resetForm, isReturnedEditMode]
    );

    const handleAction = useCallback(
        async (status = '', evaluationComment = null, isSeePermission = undefined) => {
            setSubmitted(true);
            setActionType(status);

            // Use the evaluation comment if provided (from modal), otherwise use form data
            // If evaluationComment is explicitly provided (even if empty string), use it
            // Otherwise fall back to formData.forEvaluation
            const commentToUse = evaluationComment !== null && evaluationComment !== undefined ? evaluationComment : formData.forEvaluation;

            // For approval (ACCEPTED), allocation is required
            // Exception: K-mobility users and RETURNED_TO_KARSAN don't require allocation
            if (status === WarrantyClaimStatus.ACCEPTED && !formData.alokasyon && data?.status !== WarrantyClaimStatus.RETURNED_TO_KARSAN && !isKmobilityUser) {
                return false;
            }

            // For rejection and back, evaluation comment is required (from modal or existing)
            // For approval (ACCEPTED), comment is optional
            if ((status === WarrantyClaimStatus.REJECTED || status === WarrantyClaimStatus.RETURNED) && !commentToUse) {
                return false;
            }

            // Update formData with the evaluation comment from modal
            // Update even if evaluationComment is empty string (for approval, comment is optional)
            if (evaluationComment !== null && evaluationComment !== undefined && evaluationComment !== formData.forEvaluation) {
                handleChange('forEvaluation', evaluationComment);
            }

            try {
                const success = await handleActionSave(id, status, formData?.alokasyon, commentToUse,isSeePermission);
                if (success) {
                    resetForm();
                    setActionType(null);
                }
                return success;
            } catch (err) {
                return false;
            }
        },
        [formData, handleChange, id, handleActionSave, data, resetForm, isKmobilityUser]
    );

    // Show modal handlers
    const handleShowApprovalModal = () => {
        // Check if allocation is required for approval
        // Exception: K-mobility users and RETURNED_TO_KARSAN don't require allocation
        if (!formData.alokasyon && data.status !== WarrantyClaimStatus.RETURNED_TO_KARSAN && !isKmobilityUser) {
            setSubmitted(true);
            setActionType(WarrantyClaimStatus.ACCEPTED);
            return;
        }

        setApprovalModalType('approve');
        setApprovalModalVisible(true);
    };

    const handleShowRejectionModal = () => {
        setApprovalModalType('reject');
        setApprovalModalVisible(true);
    };

    const handleShowBackModal = () => {
        setApprovalModalType('back');
        setApprovalModalVisible(true);
    };

    // Confirm handlers that execute the actual action
    const handleApprovalConfirm = (evaluationComment,isSeePermission) => {
        handleAction(30, evaluationComment,isSeePermission);
        setApprovalModalVisible(false);
    };

    const handleRejectionConfirm = (evaluationComment) => {
        handleAction(40, evaluationComment);
        setApprovalModalVisible(false);
    };

    const handleBackConfirm = (evaluationComment) => {
        handleAction(15, evaluationComment);
        setApprovalModalVisible(false);
    };

    // Handle returned warranty claims update
    const handleReturnedUpdate = async (commentText = '') => {
        setSubmitted(true);

        // Basic validation
        if (!formData.chassisNo || !formData.failureDate) return false;

        // Validation for bulletNo when showBulletNo is true (Servis Bülteni is selected)
        if (showBulletNo && !formData.bulletNo) {
            return false;
        }

        // Validation for subWarrantyType when warranty type is 'W' (Standart Garanti) or 'EX' (Extended Warranty)
        if ((String(formData?.warrantyType) === 'W' || String(formData?.warrantyType) === 'EX') && !formData.subWarrantyType) {
            return false;
        }

        // Validation for dealerPackageIds and packageWarrantyType when warranty type is 'PM'
        if (String(formData?.warrantyType) === 'PM' && (!formData.dealerPackageIds || formData.dealerPackageIds.length === 0 || !formData.packageWarrantyType)) {
            return false;
        }

        // Validation for cascading dropdowns and fault mode
        const dropdownValidation = {
            mainCategory: !formData.mainCategory,
            secondCategory: !formData.secondCategory,
            failureMode: !formData.failureMode
        };

        setCascadingDropdownErrors(dropdownValidation);

        // Check if any dropdown is missing
        if (dropdownValidation.mainCategory || dropdownValidation.secondCategory || dropdownValidation.failureMode) {
            return false;
        }

        // Validation for repairKm
        if (!formData.repairKm) {
            return false;
        }

        try {
            const payload = {
                ...formData,
                id: Number(id),
                sendToApprovalComment: commentText?.trim() || '',
                status: 1, // Status 1 means sending to approval (same as "Kaydet & Gönder")
                updatewarrantyclaimtype: true // Flag to indicate this is a returned warranty claim type update
            };

            const result = await updateWarrantyClaims(payload).unwrap();

            if (result) {
                setIsReturnedEditMode(false);
                setSubmitted(false);
                // Optionally show success message
                returnBack();
            }
            return true;
        } catch (error) {
            console.error('Returned warranty claims update error:', error);
            return false;
        }
    };

    return (
        <div className="grid" style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid col-12">
                {/* Araç Bilgileri Section */}
                <div className="col-12 mb-4">
                    {editMode && (
                        <>
                            <div className="flex align-items-center mb-3">
                                <i className="pi pi-caret-up   mr-2" style={{ fontSize: '0.8rem' }}></i>
                                <h5 className="  font-semibold m-0">{th('warrantyClaims.detail.form.sections.vehicleInfo')}</h5>
                            </div>
                            <div className="border-top-1 border-300 mb-3"></div>
                        </>
                    )}
                    <div className="grid">
                        {editMode && formData.claimBranchDealerName && <FormField label={th('warrantyReports.details.columns.DealerFullName')} content={<span>{formData.claimBranchDealerName}</span>} />}
                        <FormField
                            label={th('warrantyClaims.detail.form.warrantyType')}
                            content={
                                editMode && !isReturnedEditable ? (
                                    <span>{data?.warrTypeName}</span>
                                ) : (
                                    <>
                                        <WarrantyTypeDropdown
                                            value={formData.warrantyType}
                                            onChange={(val, warrantyTypeObj) => handleWarrantyTypeChange(String(val), warrantyTypeObj)}
                                            className={submitted && !formData.warrantyType ? 'p-invalid w-full' : 'w-full'}
                                            chassisNo={formData.chassisNo}
                                            claimDate={formData.claimDate}
                                            kmInformation={formData.kmInformation}
                                        />
                                        {submitted && !formData.warrantyType && <small className="p-error">{th('warrantyClaims.detail.validation.warrantyTypeRequired')}</small>}
                                    </>
                                )
                            }
                            required={!editMode || isReturnedEditable}
                        />
                        {subWarrantyTypeVisible && (
                            <FormField
                                label={th('warrantyClaims.detail.form.subWarrantyType')}
                                content={
                                    editMode ? (
                                        isReturnedEditable ? (
                                            <>
                                                <SubWarrantyTypeDropdown
                                                    value={formData.subWarrantyType}
                                                    onChange={(code) => handleChange('subWarrantyType', code)}
                                                    placeholder={th('warrantyClaims.detail.form.subWarrantyTypePlaceholder')}
                                                    className={submitted && !formData.subWarrantyType ? 'p-invalid w-full' : 'w-full'}
                                                    chassisNo={formData.chassisNo}
                                                    claimDate={formData.claimDate}
                                                    kmInformation={formData.kmInformation}
                                                />
                                                {submitted && !formData.subWarrantyType && <small className="p-error">{th('warrantyClaims.detail.validation.subWarrantyTypeRequired')}</small>}
                                            </>
                                        ) : (
                                            <span>{data?.subWarrantyTypeName || '-'}</span>
                                        )
                                    ) : (
                                        <>
                                            <SubWarrantyTypeDropdown
                                                value={formData.subWarrantyType}
                                                onChange={(code) => handleChange('subWarrantyType', code)}
                                                placeholder={th('warrantyClaims.detail.form.subWarrantyTypePlaceholder')}
                                                className={submitted && !formData.subWarrantyType ? 'p-invalid w-full' : 'w-full'}
                                                chassisNo={formData.chassisNo}
                                                claimDate={formData.claimDate}
                                                kmInformation={formData.kmInformation}
                                            />
                                            {submitted && !formData.subWarrantyType && <small className="p-error">{th('warrantyClaims.detail.validation.subWarrantyTypeRequired')}</small>}
                                        </>
                                    )
                                }
                                required={!editMode || isReturnedEditable}
                            />
                        )}

                        {showPmDropdown && (
                            <FormField
                                label={th('warrantyClaims.detail.form.dealerPackage')}
                                content={
                                    editMode && !isReturnedEditable ? (
                                        <span>{formData.dealerPackageName || '-'}</span>
                                    ) : (
                                        <>
                                            <DealerPackageDropdown
                                                value={formData.dealerPackageIds}
                                                onChange={(val) => handleDealerPackageChange(val)}
                                                className={submitted && (!formData.dealerPackageIds || formData.dealerPackageIds.length === 0) ? 'p-invalid w-full' : 'w-full'}
                                                placeholder={th('warrantyClaims.detail.form.dealerPackagePlaceholder')}
                                                disabled={isAddingDealerPackageItems}
                                            />
                                            {submitted && (!formData.dealerPackageIds || formData.dealerPackageIds.length === 0) && <small className="p-error">{th('warrantyClaims.detail.form.dealerPackageRequired')}</small>}
                                        </>
                                    )
                                }
                                required={!editMode || isReturnedEditable}
                            />
                        )}

                        {showPmDropdown && (
                            <FormField
                                label={th('warrantyClaims.detail.form.packageWarrantyType')}
                                content={
                                    editMode && !isReturnedEditable ? (
                                        <span>{formData.packageWarrantyTypeName || '-'}</span>
                                    ) : (
                                        <>
                                            <WarrantyPaymentTypeRadio
                                                value={formData.packageWarrantyType}
                                                onChange={(val) => handlePackageWarrantyTypeChange(val)}
                                                className={submitted && !formData.packageWarrantyType ? 'p-invalid w-full' : 'w-full'}
                                                disabled={isAddingDealerPackageItems}
                                            />
                                            {submitted && !formData.packageWarrantyType && <small className="p-error">{th('warrantyClaims.detail.form.packageWarrantyTypeRequired')}</small>}
                                        </>
                                    )
                                }
                                required={!editMode || isReturnedEditable}
                            />
                        )}

                        {editMode && data?.warrClaimsNo && <FormField label={th('warrantyClaims.detail.form.warrantyClaimNo')} content={<span>{data?.warrClaimsNo}</span>} />}

                        <FormField
                            label={th('warrantyClaims.table.preApprovalNo')}
                            content={
                                editMode ? (
                                    totalsData?.data?.isPreAppRequired && (data?.status === WarrantyClaimStatus.DRAFT || isReturnedEditable) ? (
                                        <PreApprovalDropdown value={formData.preAppNo} onChange={(val) => handleChange('preAppNo', val)} chassisNo={formData.chassisNo} className="w-full" placeholder={th('warrantyClaims.placeholders.preApprovalNo')} />
                                    ) : (
                                        <span>{formData.preAppNo || '-'}</span>
                                    )
                                ) : (
                                    <>
                                        <PreApprovalDropdown
                                            value={formData.preAppNo}
                                            onChange={(val) => handleChange('preAppNo', val)}
                                            chassisNo={formData.chassisNo}
                                            className={submitted && String(formData?.warrantyType) === 'G' && !formData.preAppNo ? 'p-invalid w-full' : 'w-full'}
                                            placeholder={th('warrantyClaims.placeholders.preApprovalNo')}
                                        />
                                        {submitted && String(formData?.warrantyType) === 'G' && !formData.preAppNo && <small className="p-error">{th('warrantyClaims.detail.validation.preApprovalNoRequired')}</small>}
                                    </>
                                )
                            }
                            required={!editMode && String(formData?.warrantyType) === 'G'}
                        />

                        {editMode && data?.customerStatementId && (
                            <FormFieldPair
                                firstLabel={th('warrantyClaims.detail.form.workOrder')}
                                firstContent={
                                    <span
                                        className="cursor-pointer text-blue-500 hover:text-blue-700 underline"
                                        onClick={handleOpenCustomerDeclarationDialog}
                                        style={{ cursor: 'pointer', color: '#0B3187', textDecoration: 'underline' }}
                                        title={th('warrantyClaims.detail.form.customerDeclarationLinkTitle')}
                                    >
                                        {data?.customerStatementId}
                                    </span>
                                }
                                secondLabel={th('warranty.customerDeclaration.tooltips.closingForm')}
                                secondContent={
                                    <i
                                        onClick={() => handleViewClosingFormPdf(data.customerStatementId)}
                                        className="pi pi-file-pdf text-red-500 hover:text-red-700 cursor-pointer"
                                        style={{ fontSize: '1.3rem' }}
                                        title={th('warranty.customerDeclaration.tooltips.closingForm')}
                                    />
                                }
                            />
                        )}
                        {(vehicleAcceptanceFormData || (isLoadingVAF && shouldSearchVAF) || (!vehicleAcceptanceFormData && !isLoadingVAF && shouldSearchVAF)) && (
                            <FormField
                                label={th('warranty.vehicleAcceptanceForm.title')}
                                content={
                                    <div className="w-full">
                                        {vehicleAcceptanceFormData && (
                                            <span
                                                className="cursor-pointer text-blue-500 hover:text-blue-700 underline font-semibold"
                                                onClick={handleVAFClick}
                                                style={{ cursor: 'pointer', color: '#0B3187', textDecoration: 'underline' }}
                                                title={th('warranty.vehicleAcceptanceForm.title')}
                                            >
                                                VAF : {vehicleAcceptanceFormData.id}
                                            </span>
                                        )}
                                        {isLoadingVAF && shouldSearchVAF && !vehicleAcceptanceFormData && (
                                            <div className="flex align-items-center">
                                                <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }}></i>
                                                <span className="ml-1 text-sm text-gray-600">{th('common.loading')}</span>
                                            </div>
                                        )}
                                        {!vehicleAcceptanceFormData && !isLoadingVAF && shouldSearchVAF && <span className="text-gray-500">{th('common.noApprovedData')}</span>}
                                    </div>
                                }
                            />
                        )}
                        {(preStockInspectionData || (isLoadingPSI && shouldSearchPSI) || (!preStockInspectionData && !isLoadingPSI && shouldSearchPSI)) && (
                            <FormField
                                label={th('warrantyClaims.detail.form.preStockInspection')}
                                content={
                                    <div className="w-full">
                                        {preStockInspectionData && (
                                            <span
                                                className="cursor-pointer text-blue-500 hover:text-blue-700 underline font-semibold"
                                                onClick={handlePSIClick}
                                                style={{ cursor: 'pointer', color: '#0B3187', textDecoration: 'underline' }}
                                                title={th('warrantyClaims.detail.form.preStockInspection')}
                                            >
                                                PSI : {preStockInspectionData.id}
                                            </span>
                                        )}
                                        {isLoadingPSI && shouldSearchPSI && !preStockInspectionData && (
                                            <div className="flex align-items-center">
                                                <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }}></i>
                                                <span className="ml-1 text-sm text-gray-600">{th('common.loading')}</span>
                                            </div>
                                        )}
                                        {!preStockInspectionData && !isLoadingPSI && shouldSearchPSI && <span className="text-gray-500">{th('common.noApprovedData')}</span>}
                                    </div>
                                }
                            />
                        )}

                        {/* Warranty Warning Alerts */}
                        {editMode && !isLoadingWarrantyWarning && warrantyWarningData?.hasVafWarnings && <WarrantyWarningBanner type="VAF" claims={warrantyWarningData.vafClaims} />}
                        {editMode && !isLoadingWarrantyWarning && warrantyWarningData?.hasPsiWarnings && <WarrantyWarningBanner type="PSI" claims={warrantyWarningData.psiClaims} />}

                        <FormFieldPair
                            firstLabel={th('warrantyClaims.detail.form.chassisNo')}
                            firstContent={
                                editMode ? (
                                    <span
                                        className="cursor-pointer font-semibold"
                                        onClick={() => formData.chassisNo && setWarrantyInfoDialogVisible(true)}
                                        style={{ cursor: 'pointer', color: '#0B3187', textDecoration: 'underline' }}
                                        title={th('vehicleWarrantyInformation.title')}
                                    >
                                        {formData.chassisNo}
                                    </span>
                                ) : (
                                    <>
                                        <ChassisWithExternalDropdown
                                            disabled={true}
                                            value={formData.chassisNo}
                                            onChange={(val, fullChassisData) => handleChange('chassisNo', val)}
                                            onResolvedSelected={(fullData) => handleChange('plateNo', fullData?.licensePlate || '')}
                                            className={submitted && !formData.chassisNo ? 'p-invalid w-full' : 'w-full'}
                                        />
                                        {submitted && !formData.chassisNo && <small className="p-error">{th('warrantyClaims.detail.validation.chassisNoRequired')}</small>}
                                    </>
                                )
                            }
                            secondLabel={th('warrantyClaims.detail.form.plateNo')}
                            secondContent={<span>{formData.plateNo || '-'}</span>}
                            firstRequired={!editMode}
                        />
                        {!editMode && (
                            <FormField
                                label={th('warrantyClaims.detail.form.customerStatementNumber')}
                                content={
                                    <InputText
                                        disabled={true}
                                        value={formData.customerStatementId}
                                        onChange={(e) => handleChange('customerStatementId', e.target.value)}
                                        className="w-full"
                                        placeholder={th('warrantyClaims.detail.form.customerStatementNumber')}
                                    />
                                }
                            />
                        )}
                        <FormFieldPair
                            firstLabel={th('warrantyClaims.detail.form.repairDate')}
                            firstContent={
                                editMode ? (
                                    isReturnedEditable ? (
                                        <>
                                            <Calendar
                                                value={formData.failureDate}
                                                onChange={(e) => handleChange('failureDate', e.value)}
                                                dateFormat="dd/mm/yy"
                                                showIcon
                                                className={submitted && !formData.failureDate ? 'p-invalid w-full' : 'w-full'}
                                                placeholder={th('warrantyClaims.detail.form.repairDatePlaceholder')}
                                            />
                                            {submitted && !formData.failureDate && <small className="p-error">{th('warrantyClaims.detail.validation.repairDateRequired')}</small>}
                                        </>
                                    ) : (
                                        <span>{formData.failureDate instanceof Date && !isNaN(formData.failureDate) ? formatDateOnly(formData.failureDate) : '-'}</span>
                                    )
                                ) : (
                                    <>
                                        <Calendar
                                            value={formData.failureDate}
                                            onChange={(e) => handleChange('failureDate', e.value)}
                                            dateFormat="dd/mm/yy"
                                            showIcon
                                            className={submitted && !formData.failureDate ? 'p-invalid w-full' : 'w-full'}
                                            placeholder={th('warrantyClaims.detail.form.repairDatePlaceholder')}
                                        />
                                        {submitted && !formData.failureDate && <small className="p-error">{th('warrantyClaims.detail.validation.repairDateRequired')}</small>}
                                    </>
                                )
                            }
                            secondLabel={th('warrantyClaims.detail.form.repairKm')}
                            secondContent={
                                editMode ? (
                                    isReturnedEditable ? (
                                        <>
                                            <InputNumber
                                                value={formData.repairKm}
                                                onChange={(e) => handleChange('repairKm', e.value)}
                                                className={submitted && !formData.repairKm ? 'p-invalid w-full' : 'w-full'}
                                                placeholder={th('warrantyClaims.detail.form.repairKmPlaceholder')}
                                            />
                                            {submitted && !formData.repairKm && <small className="p-error">{th('warrantyClaims.detail.validation.repairKmRequired')}</small>}
                                        </>
                                    ) : (
                                        <span>{formData.repairKm}</span>
                                    )
                                ) : (
                                    <>
                                        <InputNumber
                                            value={formData.repairKm}
                                            onChange={(e) => handleChange('repairKm', e.value)}
                                            className={submitted && !formData.repairKm ? 'p-invalid w-full' : 'w-full'}
                                            placeholder={th('warrantyClaims.detail.form.repairKmPlaceholder')}
                                        />
                                        {submitted && !formData.repairKm && <small className="p-error">{th('warrantyClaims.detail.validation.repairKmRequired')}</small>}
                                    </>
                                )
                            }
                            firstRequired={!editMode || isReturnedEditable}
                            secondRequired={!editMode || isReturnedEditable}
                        />
                        <FormFieldPair
                            firstLabel={th('warrantyClaims.detail.form.claimDate')}
                            firstContent={
                                editMode ? (
                                    <span> {formData.claimDate instanceof Date && !isNaN(formData.claimDate) ? formatDateOnly(formData.claimDate) : '-'}</span>
                                ) : (
                                    <>
                                        <Calendar
                                            value={formData.claimDate}
                                            onChange={(e) => handleChange('claimDate', e.value)}
                                            dateFormat="dd/mm/yy"
                                            showIcon
                                            disabled={true}
                                            className={submitted && !formData.claimDate ? 'p-invalid w-full' : 'w-full'}
                                            placeholder={th('warrantyClaims.detail.form.claimDatePlaceholder')}
                                        />
                                        {submitted && !formData.claimDate && <small className="p-error">{th('warrantyClaims.detail.validation.claimDateRequired')}</small>}
                                    </>
                                )
                            }
                            secondLabel={th('warrantyClaims.detail.form.failureKm')}
                            secondContent={
                                editMode ? (
                                    <span>{formData.kmInformation}</span>
                                ) : (
                                    <>
                                        <InputNumber
                                            value={formData.kmInformation}
                                            disabled={true}
                                            onChange={(e) => handleChange('kmInformation', e.value)}
                                            className={submitted && !formData.kmInformation ? 'p-invalid w-full' : 'w-full'}
                                            placeholder={th('warrantyClaims.detail.form.failureKmPlaceholder')}
                                        />
                                        {submitted && !formData.kmInformation && <small className="p-error">{th('warrantyClaims.detail.validation.failureKmRequired')}</small>}
                                    </>
                                )
                            }
                        />
                        {editMode && warrantyInfoData && (
                            <>
                                {warrantyInfoData.hasStandardWarranty && (
                                    <FormFieldPair
                                        firstLabel={th('warrantyClaims.detail.form.warrantyStartDate')}
                                        firstContent={<span>{warrantyInfoData.warrantyStartDate || '-'}</span>}
                                        secondLabel={th('warrantyClaims.detail.form.warrantyEndDate')}
                                        secondContent={<span>{warrantyInfoData.warrantyEndDate || '-'}</span>}
                                    />
                                )}

                                {warrantyInfoData.hasExtendedWarranty && <FormField label={th('warrantyClaims.detail.form.extendedWarrantyEndDate')} content={<span>{warrantyInfoData.extendedWarrantyEndDate || '-'}</span>} />}
                            </>
                        )}

                        {showBulletNo && (
                            <FormField
                                label={th('warrantyClaims.detail.form.bulletNo')}
                                content={
                                    editMode && !isReturnedEditable ? (
                                        <span>{formData.bulletNo}</span>
                                    ) : (
                                        <>
                                            <Dropdown
                                                value={formData.bulletNo}
                                                options={bulletinOptions}
                                                onChange={(e) => handleBulletNoChange(e.value || '')}
                                                className={submitted && showBulletNo && !formData.bulletNo ? 'p-invalid w-full' : 'w-full'}
                                                placeholder={th('warrantyClaims.detail.form.bulletNoPlaceholder')}
                                                loading={isLoadingBulletins || isAddingBulletinItems}
                                                showClear
                                                optionLabel="label"
                                                optionValue="value"
                                                disabled={!formData.chassisNo || isLoadingBulletins || isAddingBulletinItems}
                                            />
                                            {submitted && showBulletNo && !formData.bulletNo && <small className="p-error">{th('warrantyClaims.detail.validation.bulletNoRequired')}</small>}
                                        </>
                                    )
                                }
                                required={true}
                            />
                        )}

                        {showBulletNo && data?.serviceBulletinDescription && <FormField label={th('warrantyClaims.detail.form.serviceBulletinDescription')} content={<span>{data.serviceBulletinDescription}</span>} />}
                    </div>
                </div>
            </div>

            {/* Özet Tablolar */}
            <CostSummaryTables editMode={editMode} totalsData={totalsData} isLoadingTotals={isLoadingTotals} />

            {/* Arıza Kodları Section */}
            <div className="col-12 mb-4" style={{ display: !editMode && 'none' }}>
                {editMode && (
                    <>
                        <div className="flex align-items-center mb-3">
                            <i className="pi pi-caret-up   mr-2" style={{ fontSize: '0.8rem' }}></i>
                            <h5 className="  font-semibold m-0">{th('warrantyClaims.detail.form.sections.faultCodes')}</h5>
                        </div>
                        <div className="border-top-1 border-300 mb-3"></div>
                    </>
                )}

                <div className="grid">
                    {editMode && (data?.status === WarrantyClaimStatus.DRAFT || isReturnedEditable) && (
                        <>
                            <CascadingDropdowns
                                mainCategoryValue={formData.mainCategory}
                                secondCategoryValue={formData.secondCategory}
                                subCategoryValue={formData.subCategory}
                                subPartCategoryValue={formData.subPartCategory}
                                onMainCategoryChange={(value) => handleCascadingDropdownChange('mainCategory', value)}
                                onSecondCategoryChange={(value) => handleCascadingDropdownChange('secondCategory', value)}
                                onSubCategoryChange={(value) => handleCascadingDropdownChange('subCategory', value)}
                                onSubPartCategoryChange={(value) => handleCascadingDropdownChange('subPartCategory', value)}
                                colSize="0 0 20%"
                                isEditMode={editMode}
                                showValidation={submitted}
                                mainCategoryError={cascadingDropdownErrors.mainCategory}
                                secondCategoryError={cascadingDropdownErrors.secondCategory}
                                mainCategoryErrorMessage={th('warrantyClaims.detail.validation.mainCategoryRequired')}
                                secondCategoryErrorMessage={th('warrantyClaims.detail.validation.secondCategoryRequired')}
                            />
                            <div className="col-12" style={{ flex: '0 0 20%' }}>
                                <div className="field">
                                    <label className="block font-bold mb-2">{th('warrantyClaims.detail.form.faultMode') + ' *'}</label>
                                    <FaultModesDropdown
                                        value={formData.failureMode}
                                        onChange={(value) => handleCascadingDropdownChange('failureMode', value)}
                                        className={submitted && cascadingDropdownErrors.failureMode ? 'p-invalid w-full' : 'w-full'}
                                        placeholder={th('warrantyClaims.detail.form.faultModePlaceholder')}
                                    />
                                    {submitted && cascadingDropdownErrors.failureMode && <small className="p-error">{th('warrantyClaims.detail.validation.faultModeRequired')}</small>}
                                </div>
                            </div>
                        </>
                    )}
                    {editMode && data?.status !== WarrantyClaimStatus.DRAFT && !isReturnedEditable && (
                        <>
                            <FormField label={th('warrantyClaims.detail.form.mainCategory') + ' *'} content={<span>{formData.mainCategoryName}</span>} />
                            <FormField label={th('warrantyClaims.detail.form.secondCategory') + ' *'} content={<span>{formData.secondCategoryName}</span>} />
                            <FormField label={th('warrantyClaims.detail.form.subCategory')} content={<span>{formData.subCategoryName}</span>} />
                            <FormField label={th('warrantyClaims.detail.form.subPartCategory')} content={<span>{formData.subPartCategoryName}</span>} />
                            <FormField label={th('warrantyClaims.detail.form.faultMode') + ' *'} content={<span>{formData.failureModeName || '-'}</span>} />
                        </>
                    )}
                </div>
            </div>

            {/* İade Olanlar için Düzenleme Butonları */}
            {editMode && data?.status === WarrantyClaimStatus.RETURNED && (
                <div className="col-12 mb-4">
                    <div className="flex justify-content-end">
                        {!isReturnedEditMode ? (
                            <Button label={th('warrantyClaims.detail.buttons.edit')} onClick={() => setIsReturnedEditMode(true)} icon="pi pi-pencil" />
                        ) : (
                            <ButtonGroup
                                buttons={[
                                    {
                                        label: th('common.cancel'),
                                        onClick: () => {
                                            setIsReturnedEditMode(false);
                                            setSubmitted(false);
                                            setCascadingDropdownErrors({
                                                mainCategory: false,
                                                secondCategory: false,
                                                failureMode: false
                                            });
                                        },
                                        severity: 'gray',
                                        disabled: isReturnedUpdateLoading,
                                        icon: 'pi pi-times'
                                    },
                                    {
                                        label: `${th('warrantyClaims.detail.buttons.save')} & ${th('warrantyClaims.detail.buttons.send')}`,
                                        onClick: handleShowReturnedUpdateModal,
                                        loading: isReturnedUpdateLoading,
                                        disabled: isReturnedUpdateLoading,
                                        icon: 'pi pi-check'
                                    }
                                ]}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Müşteri Beyanı, Arıza Nedeni, Yapılan İşler Section */}
            <div className="col-12 mb-4" style={{ display: !editMode && 'none' }}>
                {editMode && (
                    <>
                        <div className="flex align-items-center mb-3">
                            <i className="pi pi-caret-up   mr-2" style={{ fontSize: '0.8rem' }}></i>
                            <h5 className="  font-semibold m-0">{th('warrantyClaims.detail.form.sections.declarationAndWork')}</h5>
                        </div>
                        <div className="border-top-1 border-300 mb-3"></div>
                    </>
                )}

                <div className="grid">
                    {editMode && data?.status !== WarrantyClaimStatus.DRAFT && <FormField label={th('warrantyClaims.detail.form.dealerComment')} content={<span>{formData.dealerComment}</span>} />}
                    {editMode && data?.status !== WarrantyClaimStatus.DRAFT && !isReturnedEditable && (
                        <>
                            <FormField label={th('warrantyClaims.detail.form.causeOfFailure')} content={<span>{formData.causeOfailure}</span>} />
                            <FormField label={th('warrantyClaims.detail.form.failureSolution')} content={<span>{formData.failureSolution}</span>} />
                        </>
                    )}
                    {editMode && data?.status === WarrantyClaimStatus.DRAFT && (
                        <FormField
                            label={
                                <>
                                    <span>{th('warrantyClaims.detail.form.dealerComment')}</span>
                                    <span className="font-normal text-sm ml-1 text-gray-500">{(formData.dealerComment || '').length}/1000</span>
                                </>
                            }
                            content={
                                <InputTextarea
                                    value={formData.dealerComment}
                                    onChange={(e) => handleChange('dealerComment', e.target.value)}
                                    className="w-full"
                                    placeholder={th('warrantyClaims.detail.form.dealerCommentPlaceholder')}
                                    rows={4}
                                    maxLength={1000}
                                />
                            }
                        />
                    )}
                    {editMode && (data?.status === WarrantyClaimStatus.DRAFT || isReturnedEditable) && (
                        <FormField
                            label={
                                <>
                                    <span>{th('warrantyClaims.detail.form.causeOfFailure')}</span>
                                    <span className="font-normal text-sm ml-1 text-gray-500"> {(formData.causeOfailure || '').length}/1000</span>
                                </>
                            }
                            content={
                                <InputTextarea
                                    value={formData.causeOfailure}
                                    onChange={(e) => handleChange('causeOfailure', e.target.value)}
                                    className="w-full"
                                    placeholder={th('warrantyClaims.detail.form.causeOfFailurePlaceholder')}
                                    rows={4}
                                    maxLength={1000}
                                />
                            }
                        />
                    )}
                    {editMode && (data?.status === WarrantyClaimStatus.DRAFT || isReturnedEditable) && (
                        <FormField
                            label={
                                <>
                                    <span>{th('warrantyClaims.detail.form.failureSolution')}</span>
                                    <span className="font-normal text-sm ml-1 text-gray-500"> {(formData.failureSolution || '').length}/1000</span>,
                                </>
                            }
                            content={
                                <InputTextarea
                                    value={formData.failureSolution}
                                    onChange={(e) => handleChange('failureSolution', e.target.value)}
                                    className="w-full"
                                    placeholder={th('warrantyClaims.detail.form.failureSolutionPlaceholder')}
                                    rows={4}
                                    maxLength={1000}
                                />
                            }
                        />
                    )}
                </div>
            </div>

            {/* Bayi - Karsan Değerlendirme Section */}
            <div className="col-12 mb-4" style={{ display: !editMode && 'none' }}>
                <div className="grid">
                    {editMode && data?.status !== WarrantyClaimStatus.DRAFT && (
                        <FormField label={th('warrantyClaims.detail.form.vehicleProductionDate')} content={<span>{data?.modifiedDate ? formatDateOnly(data.modifiedDate) : th('warrantyClaims.detail.form.noData')}</span>} />
                    )}
                    {shouldShowAllocationReadonly && (
                        <FormField
                            label={th('warrantyClaims.detail.form.allocation')}
                            content={
                                data?.status === WarrantyClaimStatus.ACCEPTED && canUpdateAllocationOnApproved && !isInvoiceIssued ? (
                                    isAllocationEditMode ? (
                                        <div className="flex align-items-center gap-2">
                                            <div className="flex-1">
                                                <AlokasyonDropdown value={allocationDraft} onChange={(val) => setAllocationDraft(val)} className="w-full" disabled={isUpdatingAllocation} />
                                            </div>
                                            <Button
                                                icon="pi pi-check"
                                                className="p-button-success p-button-sm"
                                                onClick={handleSaveAllocation}
                                                loading={isUpdatingAllocation}
                                                disabled={isUpdatingAllocation || !allocationDraft}
                                                tooltip={th('warrantyClaims.detail.buttons.save')}
                                                tooltipOptions={{ position: 'top' }}
                                            />
                                            <Button icon="pi pi-times" className="p-button-secondary p-button-sm" onClick={handleCancelAllocationEdit} disabled={isUpdatingAllocation} tooltip={th('common.cancel')} tooltipOptions={{ position: 'top' }} />
                                        </div>
                                    ) : (
                                        <div className="flex align-items-center gap-2">
                                            <span>{data?.alokasyonName || '-'}</span>
                                            <Button icon="pi pi-pencil" className="p-button-text p-button-sm" onClick={handleStartAllocationEdit} tooltip={th('warrantyClaims.detail.buttons.edit')} tooltipOptions={{ position: 'top' }} />
                                        </div>
                                    )
                                ) : (
                                    <div className="flex align-items-center gap-2">
                                        <span>{data?.alokasyonName}</span>
                                        {isInvoiceIssued && <i className="pi pi-lock text-500" title={th('warrantyClaims.detail.messages.allocationLockedInvoice')}></i>}
                                    </div>
                                )
                            }
                        />
                    )}
                    {editMode && !isInvoiceIssued && (data?.status === WarrantyClaimStatus.WAITING || data?.status === WarrantyClaimStatus.RETURNED_TO_KARSAN) && (
                        <FormField
                            label={th('warrantyClaims.detail.form.allocation') + (!isKmobilityUser ? ' *' : '')}
                            content={
                                <>
                                    <AlokasyonDropdown
                                        disabled={!data?.isMainDealerNeedApprove && !authorizationCheckforWarrantyClaims}
                                        value={formData.alokasyon}
                                        onChange={(val) => handleChange('alokasyon', val)}
                                        className={actionType === 30 && submitted && !formData.alokasyon && !isKmobilityUser ? 'p-invalid w-full' : 'w-full'}
                                    />
                                    {actionType === 30 && submitted && !formData.alokasyon && !isKmobilityUser && <small className="p-error">{th('warrantyClaims.detail.validation.allocationRequired')}</small>}
                                </>
                            }
                        />
                    )}
                </div>
            </div>

            <div className="col-12 mb-4"> {editMode && id && <ApprovalCommentsTable claimId={id} />}</div>

            {editMode &&
                ((data?.isMainDealerNeedApprove && data?.isShowMainDealerApproveButton) ||
                    (data?.status === WarrantyClaimStatus.WAITING && authorizationCheckforWarrantyClaims) ||
                    (data?.status === WarrantyClaimStatus.RETURNED_TO_KARSAN && authorizationCheckforWarrantyClaims)) && (
                    <div className="col-12 flex justify-content-start">
                        <ButtonGroup
                            buttons={[
                                {
                                    label: th('warrantyClaims.detail.buttons.approve'),
                                    icon: 'pi pi-check',
                                    onClick: handleShowApprovalModal,
                                    severity: 'success',
                                    loading: isActionLoading,
                                    disabled: isActionLoading
                                },
                                {
                                    label: th('warrantyClaims.detail.buttons.refund'),
                                    icon: 'pi pi-arrow-left',
                                    onClick: handleShowBackModal,
                                    severity: 'gray',
                                    loading: isActionLoading,
                                    disabled: isActionLoading
                                },
                                {
                                    label: th('warrantyClaims.detail.buttons.reject'),
                                    icon: 'pi pi-times',
                                    onClick: handleShowRejectionModal,
                                    severity: 'danger',
                                    loading: isActionLoading,
                                    disabled: isActionLoading
                                }
                            ]}
                        />
                    </div>
                )}

            {editMode && <div className="col-12 md:col-12 my-3 border-300" />}

            <div className="flex gap-2">
                <Button label={th('warrantyClaims.detail.buttons.return')} onClick={returnBack} className="flex-1" severity="warning" />
            </div>
            {editMode && data?.status === WarrantyClaimStatus.DRAFT && (
                <div className="flex gap-2" style={{ marginLeft: '1rem' }}>
                    <Button label={th('warrantyClaims.detail.buttons.delete')} onClick={handleDelete} className="flex-1" severity="danger" icon="pi pi-trash" />
                </div>
            )}

            {!editMode && (
                <div style={{ marginLeft: 'auto' }}>
                    <Button label={`${th('warrantyClaims.detail.buttons.saveAndContinue')}`} onClick={handleSubmit} className="w-full" loading={isCreating} disabled={isCreating} />
                </div>
            )}

            {canCreateCustomerDeclaration() && data?.status === WarrantyClaimStatus.DRAFT && (
                <div style={{ marginLeft: 'auto' }}>
                    <ButtonGroup
                        buttons={[
                            {
                                label: `${th('warrantyClaims.detail.buttons.save')} & ${th('warrantyClaims.detail.buttons.close')}`,
                                onClick: handleSubmit,
                                severity: 'gray',
                                loading: isUpdating,
                                disabled: isUpdating
                            },
                            {
                                label: `${th('warrantyClaims.detail.buttons.save')} & ${th('warrantyClaims.detail.buttons.send')}`,
                                onClick: () => handleShowSendModal(),
                                severity: 'success',
                                loading: isUpdating,
                                disabled: isUpdating
                            }
                        ]}
                    />
                </div>
            )}

            <CustomerDeclarationDialog
                visible={customerDeclarationDialogVisible}
                onHide={handleCloseCustomerDeclarationDialog}
                formData={customerDeclarationFormData}
                setFormData={setCustomerDeclarationFormData}
                errors={{}}
                handleSave={() => {}}
                editMode={true}
                handleDelete={() => {}}
                buttonsDisabled={true}
            />

            <VehicleWarrantyInfoDialog visible={warrantyInfoDialogVisible} onHide={() => setWarrantyInfoDialogVisible(false)} chassisNo={formData.chassisNo} />

            <ApprovalModal
                visible={approvalModalVisible}
                onHide={() => setApprovalModalVisible(false)}
                onConfirm={
                    approvalModalType === 'approve'
                        ? handleApprovalConfirm
                        : approvalModalType === 'reject'
                        ? handleRejectionConfirm
                        : approvalModalType === 'back'
                        ? handleBackConfirm
                        : approvalModalType === 'send'
                        ? handleSendConfirm
                        : approvalModalType === 'returnedUpdate'
                        ? handleReturnedUpdateConfirm
                        : handleApprovalConfirm
                }
                type={approvalModalType}
                currentComment={approvalModalType === 'send' ? formData.sendToApprovalComment || '' : formData.forEvaluation || ''}
                warrantyType={editMode ? data?.warrType : formData.warrantyType}
                showSeePermissionOption={hasWarrantyTeamRole()}
            />
        </div>
    );
};

export default FormWarrantyClaims;
